import { Injectable, OnDestroy } from '@angular/core';
import { Http, RequestMethod } from '@angular/http';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { BaseHttpService } from './base-http.service';
import * as fromRoot from '../store/reducers';
import { LoggerService } from './logger.service';
import { AuthService } from './auth.service';
import { AppUiActions, TestTakerActions } from '../store/actions';
import { ConfigHelper } from './config.helper';

declare var _paragonConfig: PgRendererDto.ParagonAppConfig;

@Injectable()
export class HtmlService extends BaseHttpService implements OnDestroy {

    private testTakerInfoSubscription: Subscription;
    private highlightSubscription: Subscription;
    testTakerInfo: PgRendererDto.TestTakerStationAssignment;
    serializedHighlights: PgRendererDto.HighlightDto[];
    preloadedResources: Array<{resourceId: number, objectURL: string}> = [];

    constructor(
        http: Http,
        httpClient: HttpClient,
        store: Store<fromRoot.State>,
        loggerService: LoggerService,
        authService: AuthService,
        private domSanitizer: DomSanitizer
    ) {
        super(http, httpClient, store, loggerService, authService);

        this.testTakerInfoSubscription = this.store.select(fromRoot.getTestTakerInfo).subscribe(ippTestTakerInfo => {
            this.testTakerInfo = ippTestTakerInfo;
        });

        this.highlightSubscription = this.store.select(fromRoot.getStoredHighlights).subscribe(highlights => {
            this.serializedHighlights = highlights;
        });
    }

    ngOnDestroy() {
        this.testTakerInfoSubscription.unsubscribe();
        this.highlightSubscription.unsubscribe();
    }

    private replaceInteractionPlaceHolders(html: string): string {
        return html.replace(/{i(\d+)}/ig, `<interaction [interaction]="getInteraction($1)"></interaction>`);
    }

    private replaceResourcePlaceHolders(html: string): string {
        html = this.replaceCustomTags(html);
        html = this.replaceIppCustomTags(html);
        html = html.replace(/{r(\d+):(picture|video|audio|html|pdf):?(.*)}/ig, `<resource [type]="'$2'" [id]="$1" [attr]="'$3'"></resource>`);
        return html;
    }

    private replaceCustomTags(html: string): string {
        const tabHtml = this.transformTabs(html);
        if (tabHtml && tabHtml.length > 0) {
            html = html.replace(/<tab [\s\S]*<\/tab>/igm, tabHtml);
        }
        const slideDeckPictures = this.getSlideDeckPictures(html);
        if (slideDeckPictures.length > 0) {
            html = html.replace(/<slides>([\s\S]*?)<\/slides>/igm, `<slide-deck [pics]="[${slideDeckPictures}]"></slide-deck>`);
        }
        html = html.replace(/<auto-skip-timer>(\d+)<\/auto-skip-timer>/igm, `<auto-skip-timer [time]="$1"></auto-skip-timer>`);
        html = html.replace(/<auto-play-resource\s*time="(\d+)"\s*(?:reviewTime="\d+")?\s*>\s*{r(\d+):(audio|video)}\s*<\/auto-play-resource>/igm, `<auto-play-resource [time]="$1" [resourceId]="$2" [type]="'$3'"><\/auto-play-resource>`);
        html = html.replace(/<end-of-test>{r(\d+):picture}<\/end-of-test>/igm, '<end-of-test [pictureResourceId]="$1"></end-of-test>');
        html = html.replace(/<hidden-resources>[^]*?<\/hidden-resources>/igm, '');
        return html;
    }

    private replaceIppCustomTags(html: string): string {
        html = html.replace(/<ipp-writing-sample-response title="([\s\S]*?)">{r(\d+):html}<\/ipp-writing-sample-response>/igm, `<ipp-writing-sample-response [title]="'$1'" [resourceId]="$2"></ipp-writing-sample-response>`);
        html = html.replace(/<ipp-speaking-sample-response title="([\s\S]*?)">{r(\d+):audio}<\/ipp-speaking-sample-response>/igm, `<ipp-speaking-sample-response [title]="'$1'" [resourceId]="$2"></ipp-speaking-sample-response>`);
        html = html.replace(/<ipp-transcript title="([\s\S]*?)">{r(\d+):(audio|video)}<\/ipp-transcript>/igm, `<ipp-transcript [title]="'$1'" [resourceId]="$2"></ipp-transcript>`);
        html = html.replace(/<ipp-landing-page image="{r(\d+):picture}">{r(\d+):html}<\/ipp-landing-page>/igm, `<ipp-landing-page [htmlResourceId]="$2" [pictureResourceId]="$1"></ipp-landing-page>`);
        html = html.replace(/<ipp-scoring-page>{r(\d+):html}<\/ipp-scoring-page>/igm, '<ipp-scoring-page [htmlResourceId]="$1"></ipp-scoring-page>');
        html = html.replace(/<ipp-feedback-page ready="{r(\d+):html}" almostReady="{r(\d+):html}" notReady="{r(\d+):html}">{r(\d+):html}<\/ipp-feedback-page>/igm, '<ipp-feedback-page [disclaimerResourceId]="$4" [readyResourceId]="$1" [almostReadyResourceId]="$2" [notReadyResourceId]="$3"></ipp-feedback-page>');
        html = html.replace(/<ipp-end-of-test>{r(\d+):html}<\/ipp-end-of-test>/igm, '<ipp-end-of-test [htmlResourceId]="$1"></ipp-end-of-test>');
        html = html.replace(/<ipp-download-response title="([\s\S]*?)"><\/ipp-download-response>/igm, `<ipp-download-response [title]="'$1'"></ipp-download-response>`);
        return html;
    }


    private getSlideDeckPictures(html: string): string {
        const regex = /<slide>{r(\d+):picture}<\/slide>/ig;
        let matchedArray: RegExpExecArray;
        let resultHtml = "";

        while ((matchedArray = regex.exec(html)) != null) {
            resultHtml += `${matchedArray[1]},`;
        }

        return resultHtml.substring(0, resultHtml.length - 1);
    }

    private transformTabs(html: string): string {
        const regex = /<tab title="([\s\S]*?)">([\s\S]*?)<\/tab>/ig;
        const tabNameRegex = /<tab title="([\s\S]*?)"/i;
        let matchedArray: RegExpExecArray;
        let tabIndex = 1;
        let tabHeaderHtml = "";
        let tabContentHtml = "";
        while ((matchedArray = regex.exec(html)) != null) {
            const matchedTabNameArray = tabNameRegex.exec(matchedArray[0]);
            if (matchedTabNameArray) {
                const tabName = ('_' + matchedTabNameArray[1].replace(/[\W]/g, "")).toLowerCase();
                const activeCssClass = tabIndex == 1 ? " active" : "";
                const tabTransformRegex = /<tab title="([\s\S]*?)">([\s\S]*?)<\/tab>/ig;
                tabHeaderHtml += matchedArray[0].replace(tabTransformRegex, `<li class="${activeCssClass}"><a href="#${tabName}" role="tab" data-toggle="tab" class="testlet-tab-header">$1</a></li>`);
                tabContentHtml += matchedArray[0].replace(tabTransformRegex, `<div role="tabpanel" class="tab-pane ${activeCssClass}" id="${tabName}">$2</div>`);
                tabIndex++;
            }
        }

        if (tabHeaderHtml.length > 0) {
            return `<ul class="nav nav-tabs" role="tablist">${tabHeaderHtml}</ul><div class="tab-content">${tabContentHtml}</div>`;
        }

        return null;
    }

    replaceAllPlaceHolders(html: string): string {
        return this.replaceResourcePlaceHolders(this.replaceInteractionPlaceHolders(html));
    }

    replaceImageResourcePlaceHoldersWithTags(html: string): string {
        if (ConfigHelper.isTdpMode()) {
            if (ConfigHelper.isTdpMode(true)) {
                return html.replace(/{r(\d+):picture}/ig, `<img src="${_paragonConfig.testContentServerApiUrl}/api/resource/file/${this.testTakerInfo.ClientApplicationSessionId}/$1" />`);
            } else {
                return html.replace(/{r(\d+):picture}/ig, `<img src="${_paragonConfig.testContentServerApiUrl}/api/resource/file/${this.testTakerInfo.BrandName}/${this.testTakerInfo.TestSessionSiteCode}/$1" />`);
            }
        } else {
            return html.replace(/{r(\d+):picture}/ig, `<img src="${_paragonConfig.testContentServerApiUrl}/api/resource/file/$1" />`);
        }
    }

    getResourceUrl(id: number): string {
        if (id) {
            const preloadedResource = this.preloadedResources.find(x => x.resourceId == id);
            if (preloadedResource) {
                this.preloadedResources = this.preloadedResources.filter(x => x.resourceId !== preloadedResource.resourceId);
                return preloadedResource.objectURL;
            }
            switch (_paragonConfig.mode) {
                case "tdp":
                    return `${_paragonConfig.testContentServerApiUrl}/api/resource/file/${this.testTakerInfo.BrandName}/${this.testTakerInfo.TestSessionSiteCode}/${id}`;
                case "tdp_web_delivey":
                    return `${_paragonConfig.testContentServerApiUrl}/api/resource/file/${this.testTakerInfo.ClientApplicationSessionId}/${id}`;
                case "preview":
                    return `${_paragonConfig.testContentServerApiUrl}/api/resource/file/${id}/`;
                case "ipp":
                    return `${_paragonConfig.testContentServerApiUrl}/api/resource/file/${id}/${this.testTakerInfo.AssessmentTestId}`;
                default:
                    return null;
            }
        }
        return null;
    }

    getHtmlResource(id: number): Observable<string> {
        return this.requestForText({
            method: RequestMethod.Get,
            url: this.getResourceUrl(id)
        });
    }

    getResourceTranscript(id: number): Observable<PgRendererDto.ResourceDto> {
        let url;

        switch (_paragonConfig.mode) {
            case "preview":
                url = `${_paragonConfig.testContentServerApiUrl}/api/resource/transcript/${id}`;
                break;
            case "ipp":
                url = `${_paragonConfig.testContentServerApiUrl}/api/resource/transcript/${id}/${this.testTakerInfo.AssessmentTestId}`;
                break;
            default:
                return null;
        }

        return this.request({
            method: RequestMethod.Get,
            url: url
        });
    }

    getSerializedHighlights(resourceId: number): PgRendererDto.HighlightDto {
        return this.serializedHighlights.find(x => x.ResourceId === resourceId);
    }

    setSerializedHighlights(highlightDto: PgRendererDto.HighlightDto): void {
        this.store.dispatch(new TestTakerActions.SetStoredHighlights(highlightDto));
    }

    getSafeHtml(html: string): SafeHtml {
        // eliminate script tag. source: https://github.com/jquery/jquery/blob/1.7.2/src/ajax.js#L14
        const scriptTagRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
        return this.domSanitizer.bypassSecurityTrustHtml(html.replace(scriptTagRegex, ``));
    }
}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/services/html.service.ts